import * as React from 'react'
import * as formik from 'formik'
import styled from 'styled-components'

import * as plume from '@ulule/owl-kit-components/next'
import * as model from '@owl-nest/models'
import * as api from '@owl-nest/api-client/latest'
import { t } from '@owl-nest/localize'
import * as service from '@owl-nest/services'
import { imageUploader } from '@owl-nest/behaviours'
import * as yup from '@owl-nest/validators'

import { useStep, getStepIndex, getStepProgress, Step } from '../useStep'
import { Progress } from '../components/Progress'
import { Coaching } from '../components/Coaching'
import { Actions } from '../components/Actions'
import { Tip } from '../components/Tip'

import { SolidContent } from './styles'

type ProfileProps = {
  project: model.project.ProjectForOwner
}

export function Profile({ project }: ProfileProps): React.ReactElement {
  const { setStepValue } = useStep(project)
  const auth = service.user.useAuth()
  const user = project.owner

  const [avatar, setAvatar] = React.useState<string | undefined>(undefined)

  React.useEffect(() => {
    if (user !== undefined) {
      setAvatar(model.user.avatar(user, 'full'))
    }
  }, [user])

  const { onChange: onImageChange } = imageUploader.useImageUploader({
    handleChange: async (file, url) => {
      setAvatar(await url)
    },
  })

  return (
    <>
      <Progress progress={getStepProgress(Step.PROFILE)} step={getStepIndex(Step.PROFILE)} name={t('Your profile')} />
      <Coaching project={project}>{t('Add an image and a display name to your profile.')}</Coaching>

      <formik.Formik
        initialValues={{
          username: user ? model.user.username(user) : '',
          image: undefined,
        }}
        validationSchema={yup.object({
          username: yup.string().required(),
          image: yup.file(),
        })}
        onSubmit={({ username, image }) => {
          if (auth.type === 'loggedin' && username) {
            auth.update(auth.user.id, { screenname: username })
          }
          if (auth.type === 'loggedin' && image) {
            api.api.patch.userAvatar({
              urlParams: { userId: String(auth.user.id) },
              body: { image },
            })
          }
          setStepValue()
        }}
      >
        {(formikBag) => {
          React.useEffect(() => {
            if (user !== undefined) {
              formikBag.setFieldValue('username', model.user.username(user))
            }
          }, [user])

          return (
            <Form onSubmit={formikBag.handleSubmit}>
              <SolidContent>
                <formik.Field name="image">
                  {({ field, form }: formik.FieldProps<string>) => {
                    return (
                      <plume.AvatarField
                        value={avatar}
                        onChange={(event) => {
                          const file = onImageChange(event)
                          form.setFieldValue(field.name, file)
                        }}
                        translations={{ add: t('Add a profile image') }}
                      />
                    )
                  }}
                </formik.Field>
                <formik.Field name="username">
                  {({ field, meta }: formik.FieldProps<string>) => {
                    return (
                      <plume.TextField
                        label={t('Your profile’s display name')}
                        {...field}
                        error={meta.touched && meta.error}
                      />
                    )
                  }}
                </formik.Field>
              </SolidContent>

              <Tip>{t('Nothing final, you can edit this information later.')}</Tip>
              <Actions>
                <plume.LinkAsButton kind="secondary" type="button" onClick={() => setStepValue()}>
                  {t('Skip')}
                </plume.LinkAsButton>
                <plume.Button kind="primary">{t('Next')}</plume.Button>
              </Actions>
            </Form>
          )
        }}
      </formik.Formik>
    </>
  )
}

const Form = styled.form`
  height: calc(100% - (132px + 4 * 22px));

  ${plume.AvatarField} {
    margin: 37px auto 27px;
    width: fit-content;
  }

  ${plume.TextField} {
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 52px;
  }
`
